import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router';
import type { z } from 'zod';
import { useForgotPassword, useResetPassword } from '../../api/queries';
import { Alert, Button, Field, Input, PasswordInput } from '../../components/ui';
import { buttonClass } from '../../components/ui/button-class';
import { errorMessage } from '../../i18n/labels';
import { AuthLayout } from './AuthLayout';
import { fieldError } from './fieldError';
import { emailField, resetSchema } from './schemas';

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const forgot = useForgotPassword();
  const [email, setEmail] = useState('');
  const parsed = emailField.safeParse(email);
  const [touched, setTouched] = useState(false);
  return (
    <AuthLayout
      title={t('auth.forgotTitle')}
      subtitle={t('auth.forgotSubtitle')}
      footer={
        <Link to="/login" className="link">
          {t('auth.backToSignIn')}
        </Link>
      }
    >
      {forgot.isSuccess ? (
        <Alert tone="success" role="status">
          {t('auth.forgotSent')}
        </Alert>
      ) : (
        <form
          className="space-y-4"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            setTouched(true);
            if (parsed.success) forgot.mutate(parsed.data);
          }}
        >
          {forgot.error && <Alert tone="danger">{errorMessage(t, forgot.error)}</Alert>}
          <Field
            label={t('auth.email')}
            error={
              touched && !parsed.success
                ? fieldError(t, parsed.error.issues[0]?.message)
                : undefined
            }
          >
            {(p) => (
              <Input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                {...p}
              />
            )}
          </Field>
          <Button type="submit" className="w-full" loading={forgot.isPending}>
            {t('auth.sendResetLink')}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const reset = useResetPassword();
  const form = useForm<z.infer<typeof resetSchema>>({
    resolver: zodResolver(resetSchema),
    defaultValues: { password: '', confirm: '' },
  });
  return (
    <AuthLayout title={t('auth.resetTitle')}>
      {!token ? (
        <Alert tone="danger">{t('errors.AUTH_TOKEN_INVALID')}</Alert>
      ) : reset.isSuccess ? (
        <div className="space-y-4">
          <Alert tone="success" role="status">
            {t('auth.resetDone')}
          </Alert>
          <Link to="/login" className={buttonClass('primary') + ' w-full'}>
            {t('auth.signIn')}
          </Link>
        </div>
      ) : (
        <form
          className="space-y-4"
          noValidate
          onSubmit={form.handleSubmit((v) => reset.mutate({ token, password: v.password }))}
        >
          {reset.error && <Alert tone="danger">{errorMessage(t, reset.error)}</Alert>}
          <Field
            label={t('auth.newPassword')}
            hint={t('auth.passwordHint')}
            error={fieldError(t, form.formState.errors.password?.message)}
          >
            {(p) => (
              <PasswordInput autoComplete="new-password" {...p} {...form.register('password')} />
            )}
          </Field>
          <Field
            label={t('auth.confirmPassword')}
            error={fieldError(t, form.formState.errors.confirm?.message)}
          >
            {(p) => (
              <PasswordInput autoComplete="new-password" {...p} {...form.register('confirm')} />
            )}
          </Field>
          <Button type="submit" className="w-full" loading={reset.isPending}>
            {t('auth.setPassword')}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
